import type { CommunityDetail } from '@pba/react-network';
import type { CommunityList } from 'models';
import type { User } from 'features/auth';
import { canCreateContentObject } from '../content-object/permissions';
import { hasMembershipInMultipleCommunitiesProfile } from '../user-roles';
import {
  isCommunitiesAdmin,
  isCommunityAdminProfile,
  isOrganizationAdminProfile,
  isOrganizationsAdmin,
  isSuperAdmin,
} from '../utils';

/** @deprecated Detail and list do not have a proper inheritance. */
type ManageableCommunity = {
  id: string;
  organization: { id: string };
};

export const canViewAdminDashboardCommunities = ({ viewer }: { viewer: User }) =>
  isSuperAdmin({ viewer: viewer.railsUserData }) ||
  isOrganizationsAdmin({ viewer: viewer.railsUserData }) ||
  isCommunitiesAdmin({ viewer: viewer.railsUserData });

export const canManageCommunityDetail = ({ community, viewer }: { community: ManageableCommunity; viewer: User }) =>
  isSuperAdmin({ viewer: viewer.railsUserData }) ||
  isCommunityAdminProfile({ communityId: community.id, viewer: viewer.railsUserData }) ||
  isOrganizationAdminProfile({ organizationId: community.organization.id, viewer: viewer.railsUserData });

export const canManageCommunity = ({ community, viewer }: { community: CommunityList; viewer: User }) =>
  canManageCommunityDetail({ community, viewer });

export const canManageCommunityAssets = ({ community, viewer }: { community: ManageableCommunity; viewer: User }) =>
  canManageCommunityDetail({ community, viewer });

export const canInviteToCommunity = ({
  community,
  viewer,
}: {
  community: { privacy: string } & ManageableCommunity;
  viewer: User;
}) => community.privacy === 'public' || canManageCommunityDetail({ community, viewer });

export const canViewCommunityMembers = ({
  community,
  viewer,
}: {
  community: { settings: { viewMembers?: boolean } } & ManageableCommunity;
  viewer: User;
}) => Boolean(community.settings.viewMembers) || canManageCommunityDetail({ community, viewer });

export const canLeaveCommunity = ({
  community,
  viewer,
}: {
  community: { settings: { allowUnsubscribe?: boolean } } & ManageableCommunity;
  viewer: User;
}) =>
  hasMembershipInMultipleCommunitiesProfile({ viewer: viewer.railsUserData }) &&
  (Boolean(community.settings.allowUnsubscribe) || canManageCommunityDetail({ community, viewer }));

export const canCreatePostInCommunity = ({ community, viewer }: { community: CommunityDetail; viewer: User }) =>
  canCreateContentObject({ viewer }) &&
  (community.settings.allowPosts || canManageCommunityDetail({ community, viewer }));

export const canLeaveCommunityDetail = ({ community, viewer }: { community: CommunityDetail; viewer: User }) =>
  canLeaveCommunity({ community, viewer });

export const canManageSelectedCommunity = ({
  community,
  viewer,
}: {
  community?: { organization: { id: string }; value: string };
  viewer: User;
}) =>
  community !== undefined &&
  canManageCommunityDetail({
    community: { id: community.value, organization: community.organization },
    viewer,
  });
